// config.tsx
import { TFunction } from 'i18next';

import { PaywallTypes } from '@/enums/apiEnums.ts';
import { PackageDeals } from '@/api/login/types.ts';
import { OFFERING_IDS } from '@/constants/offering_ids.ts';
import { TelegramCustomStar } from '@/components/common/svg/telegram/TelegramCustomStar.tsx';
import FireReactionShape from '@/components/common/svg/shapes/FireReactionShape.tsx';
import PriorityLikesShape from '@/components/common/svg/shapes/paywall/PriorityLikesShape.tsx';
import HeartSimpleShape from '@/components/common/svg/shapes/HeartSimpleShape.tsx';
import InfinityShape from '@/components/common/svg/shapes/paywall/InfinityShape.tsx';
import Send from '@/components/common/svg/Send.tsx';
import LightningShape from '@/components/common/svg/shapes/paywall/LightningShape.tsx';
import { CameraVideoFilledShape } from '@/components/common/svg/shapes/CameraVideoShape.tsx';

type PaywallConfig = {
  [key in PaywallTypes]: {
    id: number;
    mainIcon: JSX.Element;
    mainIconGradient: string;
    buttonText: string;
    getDescription: (p: PackageDeals, t?: TFunction) => string;
    icon: (params: IconParams) => JSX.Element;
    benefits: {
      header: string;
      content: {
        before?: JSX.Element;
        description: string;
        additionalText?: string;
      }[];
    };
    headerText: string;
    subtitleText: string;
  };
};

export type IconParams = {
  isPreSelected: boolean;
  description?: string;
};

export const paywallConfig: PaywallConfig = {
  [PaywallTypes.TYPE_ONE_TIME]: {
    id: OFFERING_IDS.giftId,
    mainIcon: <FireReactionShape height={33} width={33} fillColor="#fff" />,
    mainIconGradient: 'linear-gradient(90deg, #FDCB40 5.42%, #FFB534 93.75%)',
    headerText: 'paywall.title.gift',
    subtitleText: 'paywall.subtitle.gift',
    buttonText: 'paywall.button.gift',
    getDescription: (p, t) => {
      const quantity = p.quantity;
      let reactionWord = '';

      if (!t) return reactionWord;

      if (quantity % 10 === 1 && quantity % 100 !== 11) {
        reactionWord = t('reaction'); // 1 reaction
      } else if (
        quantity % 10 >= 2 &&
        quantity % 10 <= 4 &&
        (quantity % 100 < 10 || quantity % 100 >= 20)
      ) {
        reactionWord = t('reactions_2_4'); // 2-4 reactions
      } else {
        reactionWord = t('reactions'); // 0, 5+ reactions
      }

      return `${quantity} ${reactionWord}`;
    },
    icon: ({ description }) => (
      <div style={{ color: '#FFF', fontSize: '16px', fontWeight: '500' }}>
        {description}
      </div>
    ),
    benefits: {
      header: 'paywall.benefits.header.gift',
      content: [
        {
          before: (
            <Send
              height={16}
              width={16}
              fillColor="#FDC93F"
              style={{ marginRight: '2px' }}
            />
          ),
          description: 'paywall.benefits.content.get_noticed',
          additionalText: 'paywall.benefits.content.get_noticed.additional'
        },
        {
          before: <span style={{ fontWeight: 600, color: '#FDC93F' }}>x3</span>,
          description: 'paywall.benefits.content.starting_easier',
          additionalText: 'paywall.benefits.content.starting_easier.additional'
        },
        {
          before: (
            <LightningShape
              height={25}
              width={25}
              style={{ marginLeft: '1px' }}
            />
          ),
          description: 'paywall.benefits.content.show_real_interest',
          additionalText:
            'paywall.benefits.content.show_real_interest.additional'
        }
      ]
    }
  },
  [PaywallTypes.TYPE_SUBSCRIPTION]: {
    id: OFFERING_IDS.premiumId,
    mainIcon: <TelegramCustomStar height={33} width={33} fillColor="#fff" />,
    mainIconGradient: 'linear-gradient(90deg, #8681FA 33.02%, #D16FD9 93.75%)',
    headerText: 'paywall.title.premium',
    subtitleText: 'paywall.subtitle.premium',
    buttonText: 'paywall.button.premium',
    getDescription: (p, t) =>
      `${p.product.subscription_details?.service_duration_days} ${t && t('days')}`,
    icon: ({ description }) => (
      <div style={{ color: '#FFF', fontSize: '16px', fontWeight: '500' }}>
        {description}
      </div>
    ),
    benefits: {
      header: 'paywall.benefits.header.premium',
      content: [
        {
          before: <PriorityLikesShape />,
          description: 'paywall.benefits.content.priority_likes',
          additionalText: 'paywall.benefits.content.priority_likes.additional'
        },
        {
          before: (
            <FireReactionShape
              height={19}
              width={19}
              fillColor="#FDC93F"
              style={{ marginBottom: 2 }}
            />
          ),
          description: 'paywall.benefits.content.weekly_reactions',
          additionalText: 'paywall.benefits.content.weekly_reactions.additional'
        },
        {
          before: (
            <HeartSimpleShape height={18} width={18} fillColor="#FDC93F" />
          ),
          description: 'paywall.benefits.content.see_who_liked_you',
          additionalText:
            'paywall.benefits.content.see_who_liked_you.additional'
        },
        {
          before: <InfinityShape height={22} width={22} fillColor="#FDC93F" />,
          description: 'paywall.benefits.content.unlimited_likes',
          additionalText: 'paywall.benefits.content.unlimited_likes.additional'
        },
        {
          before: (
            <CameraVideoFilledShape
              height={18}
              width={18}
              fillColor="#FDC93F"
            />
          ),
          description: 'paywall.benefits.content.watch_video',
          additionalText: 'paywall.benefits.content.watch_video.additional'
        }
      ]
    }
  }
};
